import axios from "axios";
import { DataTable } from "simple-datatables";
import { showAlert, showConfirm, handleRequestError } from "../utils/alert";
import VMasker from "vanilla-masker";

export default () => ({
  // Authorization State
  hasAccessModule: true,

  isModalOpen: false,
  isSubmitting: false,

  editMode: false,
  editId: null,

  bankAccountsList: [],

  form: {
    name: "",
    type: "checking", // Default
    institution: "",
    initial_balance: "",
  },

  dataTableInstance: null,

  async initBankAccounts() {
    const token = localStorage.getItem("jwt_token");
    if (!token) {
      window.location.href = "/client/login";
      return;
    }

    window.axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    try {
      // Proceed with normal initialization
      await this.loadTable();
    } catch (error) {
      // Captura o erro 403 e bloqueia a view
      if (error.response && error.response.status === 403) {
        this.hasAccessModule = false;
        return;
      }

      // Ignora se for problema de autenticação/pagamento (tratado pelo interceptor global)
      if (error.response && [401, 402].includes(error.response.status)) return;

      console.error("Infrastructure error:", error);
    }
  },

  // ================= HELPERS DE FORMATAÇÃO =================

  formatPrice(price) {
    return parseFloat(price).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  },

  applyMoneyMask(value) {
    if (!value) return "";
    const onlyNumbers = value.toString().replace(/\D/g, "");
    return VMasker.toMoney(onlyNumbers, {
      precision: 2,
      separator: ",",
      delimiter: ".",
      unit: "R$ ",
      zeroCents: false,
    });
  },

  handleInitialBalanceInput(event) {
    this.form.initial_balance = this.applyMoneyMask(event.target.value);
  },

  unmaskMoney(maskedValue) {
    if (!maskedValue) return 0;
    const cleanString = maskedValue
      .toString()
      .replace("R$ ", "")
      .replace(/\./g, "")
      .replace(",", ".");
    return parseFloat(cleanString);
  },

  getTypeLabel(type) {
    const types = {
      cash: '<span class="inline-flex items-center rounded-md bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20">Caixa (Físico)</span>',
      checking:
        '<span class="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">Conta Corrente</span>',
      savings:
        '<span class="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-600/20">Poupança</span>',
    };
    return types[type] || type;
  },

  async loadTable() {
    try {
      const response = await axios.get("/api/client/financial/bank-accounts");
      this.bankAccountsList = response.data.data;

      if (this.dataTableInstance) {
        this.dataTableInstance.destroy();
      }

      const tbody = document.querySelector("#bankAccountsTable tbody");
      tbody.innerHTML = this.bankAccountsList
        .map((account) => {
          return `
            <tr class="hover:bg-slate-50/50 transition-colors">
              <td class="py-4 px-4 xl:px-6">
                <p class="font-bold text-slate-800">${account.name}</p>
                <p class="text-xs text-slate-500">${account.institution || "N/A"}</p>
              </td>
              <td class="py-4 px-4 xl:px-6">
                ${this.getTypeLabel(account.type)}
              </td>
              <td class="py-4 px-4 xl:px-6">
                <p class="font-mono font-medium ${account.current_balance < 0 ? "text-red-600" : "text-slate-800"}">
                  ${this.formatPrice(account.current_balance)}
                </p>
              </td>
              <td class="text-left py-4 px-4 xl:px-6">
                <div class="flex justify-start gap-2">
                  <button
                    onclick="window.dispatchEvent(new CustomEvent('edit-bankaccount', {detail: ${account.id}}))"
                    class="inline-flex items-center justify-center rounded-md bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-orange-600 focus:outline-none"
                  >
                    Editar
                  </button>
                  <button
                    onclick="window.dispatchEvent(new CustomEvent('delete-bankaccount', {detail: ${account.id}}))"
                    class="inline-flex items-center justify-center rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-red-700 focus:outline-none"
                  >
                    Excluir
                  </button>
                </div>
              </td>
            </tr>
          `;
        })
        .join("");

      this.dataTableInstance = new DataTable("#bankAccountsTable", {
        searchable: false,
        perPageSelect: false,
        labels: {
          info: "Mostrando de {start} até {end} de {rows} contas",
          noRows: "Nenhuma conta bancária cadastrada",
          searchTitle: "Buscar...",
        },
      });
    } catch (error) {
      console.error(error);
    }
  },

  openCreate() {
    this.editMode = false;
    this.form = {
      name: "",
      type: "checking",
      institution: "",
      initial_balance: "",
    };
    this.isModalOpen = true;
  },

  openEdit(id) {
    this.editMode = true;
    this.editId = id;
    const account = this.bankAccountsList.find((c) => c.id === id);

    this.form = {
      name: account.name,
      type: account.type,
      institution: account.institution || "",
      initial_balance: this.applyMoneyMask(account.initial_balance),
    };
    this.isModalOpen = true;
  },

  async submitForm() {
    if (!this.form.name || this.form.name.trim() === "") {
      showAlert("Por favor, informe o Nome da Conta.", "warning");
      return;
    }

    if (!this.form.type) {
      showAlert("Por favor, selecione o Tipo de Conta.", "warning");
      return;
    }

    this.isSubmitting = true;

    try {
      if (this.editMode) {
        await axios.put(`/api/client/financial/bank-accounts/${this.editId}`, {
          ...this.form,
          initial_balance: this.unmaskMoney(this.form.initial_balance),
        });
        showAlert("Conta bancária atualizada com sucesso!", "success");
      } else {
        await axios.post("/api/client/financial/bank-accounts", {
          ...this.form,
          initial_balance: this.unmaskMoney(this.form.initial_balance),
        });
        showAlert("Conta bancária cadastrada com sucesso!", "success");
      }

      await this.loadTable();
      this.isModalOpen = false;
    } catch (error) {
      if (
        error.response &&
        error.response.status === 422 &&
        error.response.data.error
      ) {
        showAlert(error.response.data.error, "warning");
      } else {
        handleRequestError(error, "Erro ao guardar a conta bancária.");
      }
    } finally {
      this.isSubmitting = false;
    }
  },

  async deleteRecord(id) {
    const result = await showConfirm(
      "Excluir Conta Bancária",
      "Tem certeza? Se esta conta já possuir movimentações, a exclusão será bloqueada.",
    );

    if (result.isConfirmed) {
      try {
        await axios.delete(`/api/client/financial/bank-accounts/${id}`);
        await this.loadTable();
        showAlert("Conta bancária removida com sucesso.", "success");
      } catch (error) {
        if (
          error.response &&
          error.response.status === 422 &&
          error.response.data.error
        ) {
          showAlert(error.response.data.error, "warning");
        } else {
          handleRequestError(error, "Erro ao excluir a conta bancária.");
        }
      }
    }
  },
});
